import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { 
  selectAdjustments,
  selectAdjustmentsArticles,
  selectAdjustmentsSelectedArticles,
  selectAdjustmentsFilters,
  selectAdjustmentsPagination,
  selectAdjustmentsLoading,
  selectAdjustmentsError,
  selectAdjustmentReasons,
  selectProviders,
  selectLastAdjustmentResult,
  selectSelectedArticlesData,
  selectIsArticleSelected,
} from './store/selector';
import {
  setLoading,
  setError,
  setArticles,
  setPagination,
  setFilters,
  selectArticle,
  unselectArticle,
  selectAllArticles,
  clearSelection,
  setAdjustmentReasons,
  setProviders,
  setLastAdjustmentResult,
  clearLastAdjustmentResult,
} from './store/reducer';
import { adjustmentsApi } from './services/api';
import { AdjustmentFilters, AdjustmentBulkRequest, ContainerOriginChangeRequest } from './interfaces';

export const useAdjustmentsCore = () => {
  const dispatch = useDispatch();
  
  // Selectors
  const adjustments = useSelector(selectAdjustments);
  const articles = useSelector(selectAdjustmentsArticles);
  const selectedArticles = useSelector(selectAdjustmentsSelectedArticles);
  const filters = useSelector(selectAdjustmentsFilters);
  const pagination = useSelector(selectAdjustmentsPagination);
  const loading = useSelector(selectAdjustmentsLoading);
  const error = useSelector(selectAdjustmentsError);
  const adjustmentReasons = useSelector(selectAdjustmentReasons);
  const providers = useSelector(selectProviders);
  const lastAdjustmentResult = useSelector(selectLastAdjustmentResult);

  // Actions
  const loadArticles = useCallback(async (companyId: number, newFilters?: Partial<AdjustmentFilters>) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const currentFilters = { ...filters, ...newFilters };
      dispatch(setFilters(currentFilters));
      
      const response = await adjustmentsApi.getArticlesForAdjustment(companyId, currentFilters);
      
      dispatch(setArticles(response.articles));
      dispatch(setPagination(response));
    } catch (err: any) {
      dispatch(setError(err.message || 'Error al cargar artículos para ajuste'));
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, filters]);

  const performAdjustments = useCallback(async (companyId: number, request: AdjustmentBulkRequest) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const response = await adjustmentsApi.performAdjustments(companyId, request);
      
      dispatch(setLastAdjustmentResult(response));
      
      // Recargar artículos después del ajuste
      await loadArticles(companyId);
      
      return response;
    } catch (err: any) {
      dispatch(setError(err.message || 'Error al realizar ajustes'));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, loadArticles]);

  const changeContainerOrigin = useCallback(async (companyId: number, request: ContainerOriginChangeRequest) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      
      const response = await adjustmentsApi.changeContainerOrigin(companyId, request);
      
      // Recargar artículos después del cambio
      await loadArticles(companyId);
      
      return response;
    } catch (err: any) {
      dispatch(setError(err.message || 'Error al cambiar contenedor de origen'));
      throw err;
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, loadArticles]);

  // Quitar carga remota de motivos; usar constantes locales en el diálogo
  const loadAdjustmentReasons = useCallback(async () => {
    dispatch(setAdjustmentReasons([]));
  }, [dispatch]);

  // No se usan proveedores en esta versión
  const loadProviders = useCallback(async (_companyId: number) => {
    dispatch(setProviders([]));
  }, [dispatch]);

  // Selection actions
  const handleSelectArticle = useCallback((articleId: number) => {
    dispatch(selectArticle(articleId));
  }, [dispatch]);

  const handleUnselectArticle = useCallback((articleId: number) => {
    dispatch(unselectArticle(articleId));
  }, [dispatch]);

  const handleSelectAllArticles = useCallback(() => {
    dispatch(selectAllArticles());
  }, [dispatch]);

  const handleClearSelection = useCallback(() => {
    dispatch(clearSelection());
  }, [dispatch]);

  const handleSetFilters = useCallback((newFilters: Partial<AdjustmentFilters>) => {
    dispatch(setFilters(newFilters));
  }, [dispatch]);

  const handleClearLastResult = useCallback(() => {
    dispatch(clearLastAdjustmentResult());
  }, [dispatch]);

  // Computed selectors
  const selectedArticlesData = useSelector(selectSelectedArticlesData);
  const isArticleSelected = useCallback((articleId: number) => {
    // This will be called from components, so we need to get the state differently
    return selectedArticles.includes(articleId);
  }, [selectedArticles]);

  return {
    // State
    articles,
    selectedArticles,
    filters,
    pagination,
    loading,
    error,
    adjustmentReasons,
    providers,
    lastAdjustmentResult,
    selectedArticlesData,
    
    // Actions
    loadArticles,
    performAdjustments,
    changeContainerOrigin,
    loadAdjustmentReasons,
    loadProviders,
    handleSelectArticle,
    handleUnselectArticle,
    handleSelectAllArticles,
    handleClearSelection,
    setFilters: handleSetFilters,
    handleSetFilters,
    handleClearLastResult,
    isArticleSelected,
  };
};
